import React from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";

import { ErrorMessageBanner } from "components/forms/ErrorMessageBanner";
import { t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { CloseButton } from "mds/components/CloseButton";
import { FileUploadField } from "mds/components/FileUploadField";
import { LegacyButton } from "mds/components/LegacyButton";
import { Modal } from "mds/components/Modal";
import { toastLocalizedOperationError, toastSuccessMessage } from "utils/alerts";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";

interface BugReportFormValue {
  description: string;
}
interface BugReportModalBaseProps {
  onClose: () => void;
  useBugReporter: () => {
    submitBugReport: (description: string, attachedFiles: File[]) => Promise<void>;
  };
}
const MAX_FILE_SIZE = 50 * 1024 * 1024;
const ACCEPTED_FILE_TYPES = "application/pdf image/* video/*";

export const BugReportModalBase = ({ onClose, useBugReporter }: BugReportModalBaseProps) => {
  const { submitBugReport } = useBugReporter();
  const [attachedFiles, setAttachedFiles] = React.useState<File[]>([]);
  const [fileErrors, setFileErrors] = React.useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const form = useForm<BugReportFormValue>({ defaultValues: { description: "" } });
  const errors = form.formState.errors;

  const handleFileDrop = (files: File[]) => {
    const validFiles: File[] = [];
    const invalidFilesErrorMessages: string[] = [];
    files.forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        invalidFilesErrorMessages.push(
          `${file.name} ${t("help_center.bug_report_modal.attachment_error_message")}`,
        );
      } else {
        validFiles.push(file);
      }
    });
    if (invalidFilesErrorMessages.length > 0) setFileErrors(invalidFilesErrorMessages);
    if (validFiles.length > 0) setAttachedFiles((prev) => [...prev, ...validFiles]);
  };

  const removeFile = (index: number) =>
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));

  const onSubmit: SubmitHandler<BugReportFormValue> = async (formData) => {
    setIsSubmitting(true);
    try {
      trackEvent("Report a Problem - Submit", {
        ...BUTTON_EVENT_CATEGORY,
        messageLength: formData.description.length,
        fileCount: attachedFiles.length,
      });
      await submitBugReport(formData.description, attachedFiles);
      toastSuccessMessage(t("help_center.bug_report_modal.success"));
      onClose();
    } catch (error) {
      console.error("Failed to submit bug report:", error);
      toastLocalizedOperationError("bug_report_failure");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <Modal title={t("help_center.report_problem")} onClose={onClose}>
        <FormProvider {...form}>
          <form
            className="max-w-[500px] pt-5 text-sm font-medium leading-[1.3125rem] text-black-tint-20"
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <div>
              <label className="flex flex-col gap-1">
                <div>{t("help_center.bug_report_modal.what_went_wrong_label")}</div>
                <div className="text-xs font-normal">
                  {t("help_center.bug_report_modal.what_went_wrong_description")}
                </div>
                <textarea
                  aria-invalid={errors.description ? "true" : "false"}
                  className="w-full rounded-md border border-black-tint-90 p-2 font-medium aria-invalid:border-red"
                  {...form.register("description", {
                    required: t("error.field_required"),
                    validate: {
                      notBlank: (value: string) => {
                        if (value.trim() === "") {
                          return t("error.field_not_blank");
                        }
                      },
                    },
                  })}
                  id="description"
                  rows={6}
                />
              </label>
              <ErrorMessageBanner name="description" />
            </div>
            <div className="mt-4">
              <div className="flex flex-col gap-1">
                <div>{t("help_center.bug_report_modal.add_attachment_label")}</div>
                <div className="text-xs font-normal">
                  {t("help_center.bug_report_modal.add_attachment_description")}
                </div>
              </div>
              <FileUploadField
                accept={ACCEPTED_FILE_TYPES}
                className="mt-2"
                size="m"
                multiple
                onDrop={handleFileDrop}
              />
              {fileErrors.map((error) => (
                <Banner className="text-xs" key={error} kind="error">
                  {error}
                </Banner>
              ))}
              {attachedFiles.length > 0 && (
                <div className="mt-2 space-y-1">
                  {attachedFiles.map((file) => (
                    <div
                      className="flex items-center justify-between rounded border border-black-tint-90 p-2 text-sm"
                      key={`${file.name}-${file.size}-${file.lastModified}`}
                    >
                      <span className="truncate text-xs">{file.name}</span>
                      <CloseButton
                        aria-label={t("common.remove")}
                        onClick={() => removeFile(attachedFiles.indexOf(file))}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-8">
              <LegacyButton kind="secondary" size="xs" title={t("common.cancel")} onClick={onClose}>
                {t("common.cancel")}
              </LegacyButton>
              <LegacyButton
                disabled={isSubmitting}
                kind="primary"
                size="xs"
                title={t("common.submit")}
                type="submit"
              >
                {t("common.submit")}
              </LegacyButton>
            </div>
          </form>
        </FormProvider>
      </Modal>
    </div>
  );
};
